/** Compact "Mark missing" control — parks a placeholder on mfg_recipe_pending_lines. */
import { useState } from "react";
import { createManufacturingRecipePendingLine } from "@/lib/manufacturingClient";
import type {
  MfgRecipeLineRole,
  MfgRecipePendingOwnerKind,
} from "@/lib/manufacturing/types";
import {
  ghostBtnClass,
  mfgInlineInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingMarkMaterialMissingControl({
  ownerKind,
  recipeId,
  materialUseId,
  namedVariantId,
  defaultName = "",
  quantity = 1,
  role = "structural",
  slotLabel = "",
  disabled,
  onCreated,
  onError,
}: {
  ownerKind: MfgRecipePendingOwnerKind;
  recipeId?: string | null;
  materialUseId?: string | null;
  namedVariantId?: string | null;
  defaultName?: string;
  quantity?: number;
  role?: MfgRecipeLineRole;
  slotLabel?: string;
  disabled?: boolean;
  onCreated?: (materialName: string) => void;
  onError?: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    const material_name = name.trim();
    if (!material_name) {
      onError?.("Describe the missing material");
      return;
    }
    setBusy(true);
    const r = await createManufacturingRecipePendingLine({
      owner_kind: ownerKind,
      recipe_id: recipeId ?? undefined,
      material_use_id: materialUseId ?? undefined,
      named_variant_id: namedVariantId ?? undefined,
      material_name,
      notes: notes.trim() || undefined,
      quantity: Number.isFinite(quantity) && quantity !== 0 ? quantity : 1,
      role,
      slot_label: slotLabel || undefined,
    });
    setBusy(false);
    if (!r.ok) {
      onError?.(r.error || "Failed to mark missing");
      return;
    }
    setOpen(false);
    setNotes("");
    onCreated?.(material_name);
  }

  if (!open) {
    return (
      <button
        type="button"
        className={ghostBtnClass}
        disabled={disabled}
        title="Park a placeholder when no catalog material matches"
        onClick={() => {
          setName(defaultName);
          setOpen(true);
        }}
      >
        Mark missing
      </button>
    );
  }

  return (
    <div className="mt-1 w-full min-w-[220px] space-y-1 rounded-md border border-amber-200 bg-amber-50/50 p-2 dark:border-amber-800 dark:bg-amber-950/20">
      <label className="block text-[11px] font-semibold text-amber-950 dark:text-amber-100">
        Missing material
        <input
          className={`${mfgInlineInputClass} mt-0.5 w-full`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="What do you need?"
          disabled={busy}
          autoFocus
        />
      </label>
      <label className="block text-[11px] text-amber-900 dark:text-amber-200">
        Description note (optional)
        <input
          className={`${mfgInlineInputClass} mt-0.5 w-full`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Specs, vendor, size…"
          disabled={busy}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy || !name.trim()}
          onClick={() => void submit()}
        >
          {busy ? "Saving…" : "Save placeholder"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
